import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber,
  message, Typography, Row, Col, Switch, Popconfirm, Divider, Drawer
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  CreditCardOutlined, ReloadOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import superAdminApi, { AdminPlan } from '../../api/superAdminApi';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import MobileDataList from '../../../../components/mobile/MobileDataList';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PlanManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<AdminPlan | null>(null);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [viewingPlan, setViewingPlan] = useState<AdminPlan | null>(null);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await superAdminApi.getPlans(true);
      if (response.success) {
        setPlans(response.data);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleCreate = () => {
    setSelectedPlan(null);
    form.resetFields();
    form.setFieldsValue({
      features: '',
      isActive: true,
      isRecommended: false
    });
    setModalVisible(true);
  };

  const handleEdit = (record: AdminPlan) => {
    setSelectedPlan(record);
    form.setFieldsValue({
      ...record,
      features: Array.isArray(record.features) ? record.features.join('\n') : ''
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        features: values.features ? values.features.split('\n').filter((f: string) => f.trim()) : []
      };

      if (selectedPlan) {
        await superAdminApi.updatePlan(selectedPlan.id, data);
        message.success('Plan updated successfully');
      } else {
        await superAdminApi.createPlan(data);
        message.success('Plan created successfully');
      }
      setModalVisible(false);
      fetchPlans();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleDelete = async (record: AdminPlan) => {
    try {
      await superAdminApi.deletePlan(record.id);
      message.success('Plan deleted successfully');
      fetchPlans();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete plan');
    }
  };

  const handleToggleActive = async (record: AdminPlan) => {
    try {
      await superAdminApi.togglePlanActive(record.id, !record.isActive);
      message.success(`Plan ${record.isActive ? 'deactivated' : 'activated'}`);
      fetchPlans();
    } catch (error: any) {
      message.error(error.message || 'Failed to update status');
    }
  };

  const columns: ColumnsType<AdminPlan> = [
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{record.displayName}</Text>
            {record.isRecommended && <Tag color="gold">Recommended</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.name}</Text>
        </Space>
      )
    },
    {
      title: 'Monthly Price',
      dataIndex: 'priceMonthly',
      key: 'priceMonthly',
      width: 120,
      render: (price) => `$${Number(price).toLocaleString()}`
    },
    {
      title: 'Yearly Price',
      dataIndex: 'priceYearly',
      key: 'priceYearly',
      width: 120,
      render: (price) => `$${Number(price).toLocaleString()}`
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <Text>Users: {record.maxUsers || 'Unlimited'}</Text>
          <Text>Leads: {record.maxLeads?.toLocaleString() || 'Unlimited'}</Text>
          <Text>AI Queries: {record.maxAIQueries?.toLocaleString() || 'Unlimited'}</Text>
        </Space>
      )
    },
    {
      title: 'Active Subs',
      dataIndex: 'activeSubscriptionCount',
      key: 'activeSubscriptionCount',
      width: 100,
      align: 'center',
      render: (count) => <Tag>{count || 0}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this plan?"
            description="Only plans without active subscriptions can be deleted."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button type="text" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Handle view plan details (mobile)
  const handleView = (plan: AdminPlan) => {
    setViewingPlan(plan);
    setDetailDrawerVisible(true);
  };

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'displayName' as const,
    secondaryField: 'name' as const,
    statusField: 'isActive' as const,
    statusConfig: {
      true: { color: 'green', text: 'Active' },
      false: { color: 'red', text: 'Inactive' },
    },
    avatar: {
      icon: <CreditCardOutlined />,
    },
    fields: [
      {
        key: 'priceMonthly' as const,
        label: 'Monthly',
        render: (v: number) => `$${Number(v || 0).toLocaleString()}`,
      },
      {
        key: 'priceYearly' as const,
        label: 'Yearly',
        render: (v: number) => `$${Number(v || 0).toLocaleString()}`,
      },
      {
        key: 'maxUsers' as const,
        label: 'Max Users',
        render: (v: number | null) => v || 'Unlimited',
      },
      {
        key: 'activeSubscriptionCount' as const,
        label: 'Active Subs',
        render: (v: number) => <Tag>{v || 0}</Tag>,
      },
    ],
    onClick: handleView,
  };

  // Mobile view
  if (isMobile) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <CreditCardOutlined /> Plans
          </Title>
        </div>

        <MobileDataList
          data={plans}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No plans found"
          onRefresh={fetchPlans}
          swipeActions={{
            right: {
              icon: <EditOutlined />,
              color: '#1890ff',
              label: 'Edit',
              onClick: handleEdit,
            },
          }}
          header={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              block
              style={{ marginBottom: 12 }}
            >
              Add Plan
            </Button>
          }
        />

        {/* Plan Detail Drawer (Mobile) */}
        <Drawer
          title={viewingPlan?.displayName || 'Plan Details'}
          placement="bottom"
          height="85%"
          open={detailDrawerVisible}
          onClose={() => setDetailDrawerVisible(false)}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  setDetailDrawerVisible(false);
                  if (viewingPlan) handleEdit(viewingPlan);
                }}
              >
                Edit
              </Button>
            </Space>
          }
        >
          {viewingPlan && (
            <div>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card size="small" title="Basic Info">
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div>
                      <Text type="secondary">Internal Name</Text>
                      <div><Text strong>{viewingPlan.name}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Display Name</Text>
                      <div><Text strong>{viewingPlan.displayName}</Text></div>
                    </div>
                    {viewingPlan.description && (
                      <div>
                        <Text type="secondary">Description</Text>
                        <div><Text>{viewingPlan.description}</Text></div>
                      </div>
                    )}
                    <div>
                      <Text type="secondary">Status</Text>
                      <div>
                        <Space>
                          <Tag color={viewingPlan.isActive ? 'green' : 'red'}>
                            {viewingPlan.isActive ? 'Active' : 'Inactive'}
                          </Tag>
                          {viewingPlan.isRecommended && <Tag color="gold">Recommended</Tag>}
                        </Space>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card size="small" title="Pricing">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Text type="secondary">Monthly</Text>
                      <div><Text strong>${Number(viewingPlan.priceMonthly || 0).toLocaleString()}</Text></div>
                    </Col>
                    <Col span={12}>
                      <Text type="secondary">Yearly</Text>
                      <div><Text strong>${Number(viewingPlan.priceYearly || 0).toLocaleString()}</Text></div>
                    </Col>
                  </Row>
                </Card>

                <Card size="small" title="Resource Limits">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <Text type="secondary">Max Users</Text>
                      <div><Text strong>{viewingPlan.maxUsers || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Max Leads</Text>
                      <div><Text strong>{viewingPlan.maxLeads?.toLocaleString() || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">AI Queries</Text>
                      <div><Text strong>{viewingPlan.maxAIQueries?.toLocaleString() || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Email Campaigns</Text>
                      <div><Text strong>{viewingPlan.maxEmailCampaigns?.toLocaleString() || 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Storage</Text>
                      <div><Text strong>{viewingPlan.maxStorage ? `${viewingPlan.maxStorage} GB` : 'Unlimited'}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Active Subscriptions</Text>
                      <div><Tag>{viewingPlan.activeSubscriptionCount || 0}</Tag></div>
                    </div>
                  </div>
                </Card>

                {viewingPlan.features && viewingPlan.features.length > 0 && (
                  <Card size="small" title="Features">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {viewingPlan.features.map((feature: string, index: number) => (
                        <li key={index}><Text>{feature}</Text></li>
                      ))}
                    </ul>
                  </Card>
                )}

                <Space style={{ width: '100%' }}>
                  <Button
                    block
                    onClick={() => viewingPlan && handleToggleActive(viewingPlan)}
                  >
                    {viewingPlan.isActive ? 'Deactivate Plan' : 'Activate Plan'}
                  </Button>
                </Space>
              </Space>
            </div>
          )}
        </Drawer>

        {/* Create/Edit Modal (Full screen on mobile) */}
        <Modal
          title={selectedPlan ? 'Edit Plan' : 'Create Plan'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width="100%"
          style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
          styles={{ body: { maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' } }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="Plan Name (Internal)"
              rules={[{ required: true, message: 'Please enter plan name' }]}
            >
              <Input placeholder="e.g., STARTER, BUSINESS" disabled={!!selectedPlan} />
            </Form.Item>
            <Form.Item
              name="displayName"
              label="Display Name"
              rules={[{ required: true, message: 'Please enter display name' }]}
            >
              <Input placeholder="e.g., Starter Plan" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input placeholder="Brief description of the plan" />
            </Form.Item>
            <Form.Item
              name="features"
              label="Features (one per line)"
              extra="Enter each feature on a new line"
            >
              <TextArea rows={4} placeholder="Feature 1&#10;Feature 2&#10;Feature 3" />
            </Form.Item>

            <Divider>Pricing</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="priceMonthly" label="Monthly ($)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="priceYearly" label="Yearly ($)">
                  <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Resource Limits</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maxUsers" label="Max Users">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="∞" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxLeads" label="Max Leads">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="∞" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maxAIQueries" label="AI Queries">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="∞" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="maxEmailCampaigns" label="Emails/Month">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="∞" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="maxStorage" label="Storage (GB)">
                  <InputNumber min={0} style={{ width: '100%' }} placeholder="∞" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="sortOrder" label="Sort Order">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Stripe Integration</Divider>
            <Form.Item name="stripePriceIdMonthly" label="Stripe Price ID (Monthly)">
              <Input placeholder="price_..." />
            </Form.Item>
            <Form.Item name="stripePriceIdYearly" label="Stripe Price ID (Yearly)">
              <Input placeholder="price_..." />
            </Form.Item>

            <Divider>Options</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="isActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" /> Active
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="isRecommended" valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" /> Recommended
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <CreditCardOutlined /> Plan Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchPlans}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add Plan
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={plans}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedPlan ? 'Edit Plan' : 'Create Plan'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name (Internal)"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., STARTER, BUSINESS" disabled={!!selectedPlan} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="Display Name"
                rules={[{ required: true, message: 'Please enter display name' }]}
              >
                <Input placeholder="e.g., Starter Plan" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <Input placeholder="Brief description of the plan" />
          </Form.Item>

          <Form.Item
            name="features"
            label="Features (one per line)"
            extra="Enter each feature on a new line"
          >
            <TextArea rows={4} placeholder="Feature 1&#10;Feature 2&#10;Feature 3" />
          </Form.Item>

          <Divider>Pricing</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priceMonthly" label="Monthly Price ($)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priceYearly" label="Yearly Price ($)">
                <InputNumber min={0} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Resource Limits</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="maxUsers" label="Max Users">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxLeads" label="Max Leads">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxAIQueries" label="Max AI Queries">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="maxEmailCampaigns" label="Max Emails/Month">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxStorage" label="Max Storage (GB)">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sortOrder" label="Sort Order">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Stripe Integration</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="stripePriceIdMonthly" label="Stripe Price ID (Monthly)">
                <Input placeholder="price_..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="stripePriceIdYearly" label="Stripe Price ID (Yearly)">
                <Input placeholder="price_..." />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Options</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isActive" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" /> Active
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isRecommended" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" /> <CheckCircleOutlined /> Recommended
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PlanManagement;
